/**
 * The terrain material (TSL): satellite imagery, near-field detail, triplanar cliffs.
 *
 * ## Detail at 50 ft
 *
 * Sentinel-2 is a 10 m/px instrument, so the imagery runs out at about z14 and the ground turns
 * to coloured mud on short final — exactly where the pilot is looking hardest and exactly where
 * the eye needs texture to judge height and closure rate. A procedural detail pattern is
 * blended in at close range, at a scale far below the imagery's, and it *modulates* the imagery
 * rather than replacing it: the satellite colours still tell you whether you are over a field,
 * a forest or a car park, while the detail gives the surface a grain to measure against.
 *
 * ## Cliffs
 *
 * A detail pattern projected straight down smears into vertical streaks on anything steep,
 * which is most of what makes a mountain look like a mountain. Triplanar projection samples in
 * all three planes and blends by the surface normal, so a cliff face is sampled in its own
 * plane. It costs three evaluations instead of one, so the tier switches it off at Low, and it
 * is only ever applied to the detail layer — the imagery is already correctly draped.
 *
 * ## Geomorphing
 *
 * Each vertex carries `morphHeight`, the height it would have on the next coarser grid.
 * Blending toward it as a tile approaches its LOD switch makes the transition continuous;
 * without it the ground visibly jumps every time a tile is replaced by its parent.
 *
 * ## One factory, many materials, one shader — and why they are pooled
 *
 * Each tile needs its own imagery texture, and a three material carries one map — so tiles get
 * one material each. They are built from *shared uniform nodes*, so the generated shader code
 * is identical for every tile and three compiles exactly one **program**, while a single
 * `updateFrame` moves the sun for all of them.
 *
 * That last claim is true and it was hiding a much more expensive one. The GPU program is cached
 * on shader *source*, so building a new material per tile really does compile no new program —
 * which is why the perf harness' shader counter stayed flat and this went unnoticed. But three
 * caches the **NodeBuilderState** on the material's cache key, and that key is built from
 * `Node.customCacheKey()`, which returns the node's *instance id*. A structurally identical graph
 * built from fresh node objects therefore produces a different key, misses the cache, and runs
 * `nodeBuilder.build()` — the full TSL→WGSL code generation for both stages — **inline, inside
 * `renderer.render()`**, for every tile adopted. At the streaming rates a 500 kt run produces
 * that was the largest single source of per-frame garbage in the simulator.
 *
 * So materials are pooled and their texture is swapped, rather than rebuilt. Reuse keeps the node
 * instances, so the cache key is unchanged, the NodeBuilderState is reused and no code is
 * generated; three notices the changed texture through the binding's `generation` counter and
 * refreshes that binding alone. A released material's texture is repointed at a 1×1 placeholder
 * so the pool cannot pin decoded imagery in memory.
 *
 * Aerial perspective is deliberately **not** here: haze has to be applied after lighting and to
 * every distant surface, not just the ground, so it lives in the scene's fog node — see
 * `aerialPerspective.ts`.
 *
 * `updateFrame(alpha)`: yes, on the factory. Allocation-free.
 */

import { Color, DataTexture, DoubleSide, Vector3, type Material, type Texture } from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import {
  abs,
  attribute,
  cameraPosition,
  float,
  mix,
  normalGeometry,
  normalize,
  positionGeometry,
  positionWorld,
  pow,
  sin,
  smoothstep,
  texture,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';
import type { QualitySettings } from '../core/quality.js';
import type { CloudShadow } from './clouds/cloudShadow.js';

/**
 * Cheap, tileable value noise.
 *
 * Arithmetic rather than a texture lookup on purpose: with triplanar mapping the detail layer
 * is evaluated three times per fragment, and three dependent texture reads across a
 * full-screen terrain pass is real bandwidth on an integrated GPU. Sines are ALU work, which is
 * what a fill-rate-bound pass has going spare. Three octaves at incommensurate frequencies, so
 * the result does not read as a single repeating ripple.
 */
type Vec2Node = ReturnType<typeof vec2>;

function detailNoise(p: Vec2Node) {
  const a = sin(p.x.mul(1.0).add(p.y.mul(1.7)));
  const b = sin(p.x.mul(2.3).sub(p.y.mul(1.1)));
  const c = sin(p.x.mul(0.41).add(p.y.mul(0.63)));
  return a.mul(0.5).add(b.mul(0.3)).add(c.mul(0.2)).mul(0.5).add(0.5);
}

export interface TerrainMaterialFactoryOptions {
  settings: QualitySettings;
  /** Colour for tiles whose imagery has not arrived, so gaps read as land rather than void. */
  untexturedColor?: Color;
  /**
   * The cloud layer's ground shadow. Wired into every tile when present.
   *
   * Optional so the factory is still constructible in a test with no cloud field, and because it
   * costs nothing to leave out: the shadow's own amount uniform is what turns it on, not this.
   */
  cloudShadow?: CloudShadow;
}

export class TerrainMaterialFactory {
  /** 1 = detail on, 0 = off. A uniform, so a tier change costs no shader recompile. */
  private readonly detailAmount = uniform(1);
  /** 1 = triplanar, 0 = plain top-down projection. */
  private readonly triplanarAmount = uniform(1);
  private readonly untextured;

  private readonly materials = new Set<MeshStandardNodeMaterial>();
  /**
   * Released materials, waiting to be handed back out.
   *
   * Two pools because there are two node graphs: a tile whose imagery has landed samples a
   * texture and one whose imagery has not does not, and they are different shaders. A textured
   * material can only be reused for another textured tile.
   */
  private readonly texturedPool: MeshStandardNodeMaterial[] = [];
  private readonly untexturedPool: MeshStandardNodeMaterial[] = [];
  /** The swappable texture node of each pooled/live textured material. */
  private readonly textureNodes = new WeakMap<MeshStandardNodeMaterial, { value: Texture }>();
  /**
   * Each material's own geomorph weight.
   *
   * Per material rather than one shared uniform, because the weight is a property of *a tile* —
   * how close it is to being replaced by its parent — and every tile has a different answer. The
   * factory-level uniform this replaces could only ever hold one value for the whole world, which
   * is why it sat at 1 and the blend never ran. Uniform *values* do not enter a node graph's cache
   * key, so every tile still shares one compiled program, exactly as the per-material texture node
   * beside it already does.
   */
  private readonly morphNodes = new WeakMap<MeshStandardNodeMaterial, { value: number }>();
  /** Parked in released materials so the pool never pins a decoded `ImageBitmap`. */
  private placeholder: DataTexture | null = null;
  /** Node graphs actually generated, and materials asked for. See {@link built}. */
  private builtValue = 0;
  private requestedValue = 0;
  private settings: QualitySettings;
  private readonly cloudShadow: CloudShadow | undefined;

  constructor(options: TerrainMaterialFactoryOptions) {
    this.settings = options.settings;
    this.untextured = uniform(options.untexturedColor ?? new Color(0x6b7a5c));
    this.cloudShadow = options.cloudShadow;
    this.applyQuality(options.settings);
  }

  /**
   * Get the material for one tile. `map` is null while its imagery is still streaming.
   *
   * Takes from the pool when it can — see the note at the top of the file on why building a fresh
   * node graph per tile costs a full TSL→WGSL code generation inside `render()`.
   */
  create(map: Texture | null): MeshStandardNodeMaterial {
    this.requestedValue++;
    const pool = map === null ? this.untexturedPool : this.texturedPool;
    const reused = pool.pop();
    if (reused !== undefined) {
      // Repoint the existing texture node: same node instances, same cache key, no codegen.
      if (map !== null) {
        const node = this.textureNodes.get(reused);
        if (node !== undefined) node.value = map;
      }
      this.materials.add(reused);
      return reused;
    }
    return this.build(map);
  }

  /** Build a material from scratch. Only reached when the pool is empty. */
  private build(map: Texture | null): MeshStandardNodeMaterial {
    this.builtValue++;
    const material = new MeshStandardNodeMaterial();
    material.roughness = 0.94;
    material.metalness = 0;
    // The skirts are curtains seen from whichever side the neighbouring LOD leaves exposed;
    // there is no winding that is right for every neighbour at once.
    material.side = DoubleSide;

    // --- geomorphing, in the vertex stage --------------------------------------------
    const morphHeight = attribute<'float'>('morphHeight', 'float');
    const morph = uniform(1);
    this.morphNodes.set(material, morph as unknown as { value: number });
    material.positionNode = vec3(
      positionGeometry.x,
      mix(morphHeight, positionGeometry.y, morph),
      positionGeometry.z,
    );

    /*
     * --- near-field detail ---------------------------------------------------------------
     *
     * Two bands, two ranges, and the ranges are the whole point.
     *
     * This used to be one fade — `smoothstep(1_400, 120, distance)` — on the reasoning that past
     * 1.4 km "the imagery is the better information anyway". **That premise is false at any
     * altitude a jet flies.** Sentinel-2 is a 10 m/px instrument and this layer caps at z14, which
     * is 9.55 m/px at the equator and 7.6 m/px at San Francisco. The coarse detail octave has an
     * ~18 m wavelength, so out at 3 km the imagery is not carrying finer information than the
     * detail is — it is upsampled, and both are describing the same scale of thing.
     *
     * The cost of getting that wrong is specific: at 4700 ft AGL the ground *directly below* is
     * 1432 m away and everything ahead is 3–10 km, so the detail layer contributed **nothing to
     * any pixel on screen** at cruise. It was tuned for low-level flight and switched off exactly
     * where most of the flying happens. That is what a pilot reported as "the resolution can get
     * real shitty sometimes".
     *
     * So the two octaves now fade on their own ranges, because they alias at different distances:
     *
     *   - **coarse** (~18 m wavelength) out to 6 km. Nyquist wants ≥2 screen pixels per
     *     wavelength; at a 90° horizontal field of view across 2000 px, a pixel subtends about
     *     d/1000 metres at distance d, so an 18 m feature stays sampled above Nyquist to roughly
     *     9 km at nadir. 6 km is that with margin for grazing angles, where foreshortening bites.
     *   - **fine** (~2.3 m wavelength) stays on the old short range. It is below the imagery's
     *     own resolution and it *will* shimmer if it is pushed out — which is what the original
     *     single fade was really protecting against, correctly, for the wrong band.
     *
     * Near the camera both fades are 1 and the result is arithmetically identical to before, so
     * low-level flight looks exactly as it did.
     */
    const distance = positionWorld.sub(cameraPosition).length();
    const coarseFade = smoothstep(6_000, 400, distance).mul(this.detailAmount);
    const fineFade = smoothstep(1_400, 120, distance).mul(this.detailAmount);

    const scale = 0.34;
    const topDown = detailNoise(
      vec2(positionWorld.x.mul(scale), positionWorld.z.mul(scale)),
    );

    const worldNormal = normalize(normalGeometry);
    const weights = pow(abs(worldNormal), float(4));
    const weightSum = weights.x.add(weights.y).add(weights.z).max(1e-4);
    const triplanar = detailNoise(vec2(positionWorld.z.mul(scale), positionWorld.y.mul(scale)))
      .mul(weights.x)
      .add(topDown.mul(weights.y))
      .add(
        detailNoise(vec2(positionWorld.x.mul(scale), positionWorld.y.mul(scale))).mul(weights.z),
      )
      .div(weightSum);

    const coarse = mix(topDown, triplanar, this.triplanarAmount);
    const fine = detailNoise(vec2(positionWorld.x.mul(2.7), positionWorld.z.mul(2.7)));
    /*
     * Centred on 1 so the detail brightens and darkens the imagery rather than tinting it, and
     * written as a sum of two deviations from 1 so each octave can carry its own fade.
     *
     * Algebraically identical to the previous `mix(1, coarse*0.72 + fine*0.28) * 0.5 + 0.75, f)`
     * whenever both fades agree — expand it: 0.5·(0.72c + 0.28f) + 0.75 = 0.36c + 0.14f + 0.75,
     * and 1 + 0.36(c−0.5) + 0.14(f−0.5) is the same polynomial. Both octaves sit in [0,1], so the
     * gain still spans [0.75, 1.25].
     */
    const gain = float(1)
      .add(coarse.sub(0.5).mul(0.36).mul(coarseFade))
      .add(fine.sub(0.5).mul(0.14).mul(fineFade));

    // Two branches rather than a union, so each produces a concretely typed node.
    if (map === null) {
      material.colorNode = this.untextured.mul(gain);
    } else {
      const mapNode = texture(map);
      // Kept so the tile's imagery can be swapped on reuse without rebuilding the graph.
      this.textureNodes.set(material, mapNode as unknown as { value: Texture });
      material.colorNode = mapNode.rgb.mul(gain);
    }

    /*
     * Cloud shadows attenuate the *direct sun* term, not the albedo.
     *
     * Folding them into `colorNode` above would be one multiply and it would be wrong: it darkens
     * the skylight too, and the whole reason a real cloud shadow reads as a soft blue-grey patch
     * rather than a black hole is that the ground under a cumulus is still lit by the entire sky.
     * See `clouds/cloudShadow.ts`.
     */
    this.cloudShadow?.applyTo(material);

    this.materials.add(material);
    return material;
  }

  /**
   * Forget a tile's material when the tile is evicted.
   *
   * Not optional bookkeeping. {@link materials} exists so the factory can tear its own work
   * down, but a set that is only ever added to keeps every material the session has ever built
   * — and a material here is not a small object: it owns a TSL node graph of a hundred-odd
   * nodes, and if the tile had imagery, the graph's texture node holds the `Texture`, which
   * holds the decoded `ImageBitmap`. Disposing the material releases its GPU resources and
   * changes nothing about any of that. So an unreleased material is a quarter of a megabyte per
   * tile ever streamed, growing for as long as the session lasts, and it was the whole of the
   * heap climb this milestone set out to fix.
   */
  release(material: MeshStandardNodeMaterial): void {
    this.materials.delete(material);

    const node = this.textureNodes.get(material);
    const pool = node === undefined ? this.untexturedPool : this.texturedPool;
    if (pool.length >= MATERIAL_POOL_LIMIT) {
      material.dispose();
      return;
    }

    /*
     * Park the material's texture on a 1×1 placeholder before pooling it.
     *
     * Without this the pool is a leak with a friendly name: a released material's node still
     * holds the tile's `Texture`, which holds its decoded `ImageBitmap`, so every tile ever
     * streamed would stay resident in the pool. The placeholder is one shared pixel.
     */
    if (node !== undefined) node.value = this.getPlaceholder();
    pool.push(material);
  }

  /** The 1×1 texture parked in released materials. Built once, on first release. */
  private getPlaceholder(): DataTexture {
    let placeholder = this.placeholder;
    if (placeholder === null) {
      placeholder = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
      placeholder.needsUpdate = true;
      placeholder.colorSpace = 'srgb';
      this.placeholder = placeholder;
    }
    return placeholder;
  }

  /** Materials this factory still holds. Should track the resident tile count exactly. */
  get liveCount(): number {
    return this.materials.size;
  }

  /**
   * Node graphs actually built, and materials actually asked for.
   *
   * Two counters because conflating them produced a wrong conclusion twice. The terrain system
   * used to increment one counter per `create()` call and report it as "materials created", which
   * counts a pool *hit* and a pool *miss* identically — so the pool's reuse ratio read as exactly
   * zero whether it was working or not, and the README recorded that its benefit was unmeasured on
   * the strength of it. Measured properly, a canyon run builds 119 graphs and then stops: once the
   * tile cache is full every subsequent adoption is a reuse, and reuse passes 39 % within twenty
   * seconds of the first eviction.
   *
   * {@link built} is the one that costs: a miss runs the full TSL→WGSL code generation inside
   * `renderer.render()`. {@link requested} is the denominator.
   */
  get built(): number {
    return this.builtValue;
  }

  get requested(): number {
    return this.requestedValue;
  }

  /**
   * Set one tile's blend toward the coarse surface: 1 = its own surface, 0 = its parent's.
   *
   * Written every frame for every visible tile — a few hundred number assignments, which is why
   * this is a uniform rather than an attribute. Changing it cannot recompile anything.
   */
  setMorphFactor(material: Material, factor: number): void {
    const node = this.morphNodes.get(material as MeshStandardNodeMaterial);
    if (node === undefined) return;
    node.value = factor < 0 ? 0 : factor > 1 ? 1 : factor;
  }

  applyQuality(settings: QualitySettings): void {
    this.settings = settings;
    this.detailAmount.value = settings.terrain.detailTextures ? 1 : 0;
    this.triplanarAmount.value = settings.terrain.triplanar ? 1 : 0;
  }

  get qualitySettings(): QualitySettings {
    return this.settings;
  }

  dispose(): void {
    for (const material of this.materials) material.dispose();
    this.materials.clear();
    for (const material of this.texturedPool) material.dispose();
    for (const material of this.untexturedPool) material.dispose();
    this.texturedPool.length = 0;
    this.untexturedPool.length = 0;
    this.placeholder?.dispose();
    this.placeholder = null;
  }
}

/**
 * How many released materials each pool keeps.
 *
 * Comfortably above the churn a fast low-level run produces, and far below the resident tile
 * count, so a long session cannot accumulate materials it will never hand out again. Past the
 * limit a released material is disposed as it always was.
 */
const MATERIAL_POOL_LIMIT = 96;

/** Sun direction shared by the terrain and water materials — set once, read by both. */
export const SUN_DIRECTION = /* #__PURE__ */ uniform(new Vector3(0.42, 0.66, 0.28).normalize());

export function setSunDirection(direction: Vector3): void {
  (SUN_DIRECTION.value as Vector3).copy(direction).normalize();
}
